// PaktMark.tsx — The Pakt symbol.
// Two interlocking C shapes + 12 hidden rays (subtle Marian reference).
// Never remove the rays — they are the brand's DNA.

import React from 'react';

export type PaktMarkVariant = 'orange' | 'mantle' | 'outline' | 'paper' | 'dark';

export interface PaktMarkProps {
  size?: number;
  variant?: PaktMarkVariant;
  /** Override ray opacity. Leave undefined for the per-variant default. */
  rayOpacity?: number;
  /** Corner radius as a fraction of size. Default 0.22. */
  radius?: number;
  /** Stroke width of the C shapes. Default 8. */
  stroke?: number;
  className?: string;
  style?: React.CSSProperties;
}

const PALETTES: Record<PaktMarkVariant, { bg: string; fg: string; dot: string; rays: number }> = {
  orange:  { bg: '#FF5B2E',    fg: '#FFF8F0', dot: '#FFF8F0', rays: 0.18 },
  mantle:  { bg: '#0B1340',    fg: '#FFF8F0', dot: '#D4B26A', rays: 0.28 },
  outline: { bg: 'transparent', fg: '#0A0A0A', dot: '#FF5B2E', rays: 0 },
  paper:   { bg: '#FFF8F0',    fg: '#FF5B2E', dot: '#FF5B2E', rays: 0.14 },
  dark:    { bg: '#0A0A0A',    fg: '#FF5B2E', dot: '#FF5B2E', rays: 0.24 },
};

export function PaktMark({
  size = 64,
  variant = 'orange',
  rayOpacity,
  radius = 0.22,
  stroke = 8,
  className,
  style,
}: PaktMarkProps) {
  const pal = PALETTES[variant];
  const rays = Array.from({ length: 12 }, (_, i) => {
    const t = (i / 12) * Math.PI * 2 - Math.PI / 2;
    const r1 = 18, r2 = 46;
    return {
      x1: 60 + Math.cos(t) * r1, y1: 60 + Math.sin(t) * r1,
      x2: 60 + Math.cos(t) * r2, y2: 60 + Math.sin(t) * r2,
    };
  });
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={className}
      style={{ display: 'block', ...style }}
      aria-label="Pakt"
    >
      {variant !== 'outline' && (
        <rect width="120" height="120" rx={120 * radius} fill={pal.bg} />
      )}
      <g opacity={rayOpacity ?? pal.rays}>
        {rays.map((r, i) => (
          <line
            key={i}
            x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2}
            stroke={pal.fg} strokeWidth="0.9" strokeLinecap="round"
          />
        ))}
      </g>
      <path
        d="M 30 34 L 58 34 L 58 54 L 48 60 L 58 66 L 58 86 L 30 86"
        fill="none" stroke={pal.fg} strokeWidth={stroke}
        strokeLinejoin="miter" strokeLinecap="square"
      />
      <path
        d="M 90 34 L 62 34 L 62 54 L 72 60 L 62 66 L 62 86 L 90 86"
        fill="none" stroke={pal.fg} strokeWidth={stroke}
        strokeLinejoin="miter" strokeLinecap="square"
      />
      <circle cx="60" cy="60" r="3" fill={pal.dot} />
    </svg>
  );
}
